//-- Copyright (c) 2021 TMT Insights Group, LLC
import StatusReasonPopover from '@/components/_common/Tasks/StatusReasonPopover';
import { StatusColors } from '@/constants/constants';
import { selectStatuses } from '@/redux/selectors/constantsSelectors';
import styled from '@emotion/styled';
import type { GridProps, Palette } from '@mui/material';
import { Box, Grid } from '@mui/material';
import { useMemo } from 'react';
import PolarisIcon from '../PolarisIcon';
import ConditionalTooltip from '../ConditionalTooltip/ConditionalTooltip';
import { useAppSelector } from '@/redux/hooks';

export type TableStatusProps = GridProps & {
  status: string;
  colorOverride?: string;
  customStatuses?: any;
  labelOverride?: string;
  enableWrap?: boolean;
  dotOnly?: boolean;
  statusReason?: string;
  statusReasonAppend?: React.ReactNode;
  textOnly?: boolean;
};

const TableStatus = ({
  status,
  colorOverride,
  customStatuses,
  labelOverride,
  statusReason,
  statusReasonAppend,
  enableWrap = false,
  dotOnly = false,
  textOnly = false
}: TableStatusProps) => {
  const statuses = customStatuses || useAppSelector(selectStatuses);
  const isFailed = status?.includes('failed') || status.toLowerCase().includes('error');

  const displayStatus = useMemo(() => {
    if (!status || !status?.length) return 'unknown';
    return status;
  }, [status]);

  // TODO: we shouldn't get this far if constants haven't been retrieved yet
  if (!statuses || !displayStatus) return null;

  const getColor = () => {
    if (colorOverride) return colorOverride;
    if (status === 'skipped') return 'default';
    if (status === 'rejected' || status.toLowerCase().includes('error')) return 'error';
    return StatusColors[displayStatus as keyof typeof StatusColors] || 'warning';
  };

  const getIcon = () => {
    if (!statusReason && status !== 'rejected') return;

    let icon = {
      icon: 'note',
      color: 'default.main'
    };

    if (statusReason && status !== 'resolved') {
      icon = {
        icon: 'error',
        color: 'warning.main'
      };
    } else if (status === 'rejected' || status.toLowerCase().includes('error')) {
      icon = {
        icon: 'cancel',
        color: 'error.main'
      };
    }

    return (
      <div>
        <Box sx={{ color: icon.color, display: 'flex', alignItems: 'center' }}>
          <PolarisIcon icon={icon.icon} height={16} />
        </Box>
      </div>
    );
  };

  const getLabel = () => {
    if (status === 'rejected') return 'Rejected';
    else if (labelOverride) return labelOverride;
    return statuses[displayStatus] || displayStatus;
  };

  return (
    <StatusReasonPopover statusReason={statusReason} isFailed={isFailed} statusReasonAppend={statusReasonAppend}>
      <ConditionalTooltip show={!!statusReason} title="Click for details" arrow>
        <WrappableText enabled={+enableWrap}>
          <Grid container alignItems="center" flexWrap="nowrap" sx={{ display: 'flex', alignItems: 'center', columnGap: 0.75 }}>
            <Status
              color={getColor()}
              status={displayStatus}
              style={{ opacity: displayStatus === 'deleted' || displayStatus === 'cancelled' ? 0.6 : 1 }}
              wrap={+enableWrap}
              dotOnly={+dotOnly}
              textOnly={+textOnly}
            >
              {!dotOnly && getLabel()}
            </Status>
            {getIcon()}
          </Grid>
        </WrappableText>
      </ConditionalTooltip>
    </StatusReasonPopover>
  );
};

export default TableStatus;

const Status = styled.span<{ status: string; wrap: number; dotOnly: number; textOnly: number }>`
  font-size: 92%;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  white-space: ${({ wrap }) => (wrap ? 'normal' : 'nowrap')};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  overflow: hidden;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  line-height: 1.2;
  text-align: left;
  ${({ theme }) => theme.customOverrides?.TableStatus};

  ${({ textOnly, dotOnly, theme }) =>
    !textOnly &&
    `
      &:before {
        content: '';
        height: ${dotOnly ? '10px' : '6px'};
        width: ${dotOnly ? '10px' : '6px'};
        min-width: 6px;
        border-radius: 50%;
        display: inline-block;
        background: ${theme.palette.text.primary};
        margin-right: 5px;
      }
  `}

  ${({ color, theme }) => {
    return `
    color: ${
      theme.palette.mode === 'dark' || color !== 'warning'
        ? theme.palette[color as keyof Palette]?.main
        : theme.palette[color as keyof Palette]?.light
    };
    &:before {
      background: ${theme.palette[color as keyof Palette]?.main};
    }
  `;
  }}
`;
// style={{ display: enableWrap ? 'inherit' : 'flex', alignItems: 'center', height: !enableWrap ? 20 : 'auto' }}
const WrappableText = styled.div<{ enabled: number }>`
  display: ${({ enabled }) => (enabled ? 'inherit' : 'flex')};
  height: ${({ enabled }) => (enabled ? 'auto' : '20px')};
`;
